/**
 * 로그인 화면. URL `/login`.
 *
 * 진입: `/`(비로그인 방문자의 랜딩)에서 로그인 버튼으로 들어오거나, 다른 화면에서
 * 401 응답 후 토큰 갱신까지 실패했을 때 강제로 이동해 온다(handleAPIError.ts 참고).
 * 나가는 곳: 로그인 성공 시 역할이 학생이면 `/`, 선생님이면 `/teacher`. 단, 응답의
 * `needPasswordChange` 가 true(관리자가 초기 비밀번호를 지정한 계정)면 역할과 무관하게
 * `/password-setup`(강제 변경 화면)으로 먼저 보낸다. 회원가입 링크(`/signin`)와 아이디/비밀번호 찾기 링크(`/find`)는 현재 임시 비활성화 상태이다.
 * 화면에는 안 보이지만 라우트 자체는 살아 있다(아래 주석 처리된 블록 참고).
 *
 * 데이터: `./api/sign/authUser`(로그인 API), `./api/home/fetchUserInfo`(로그인 후 유저 정보
 * 조회). 둘 다 `pages/api/` 아래 있지만 서버 함수가 아니라 그냥 클라이언트에서 호출하는
 * axios 모듈이다. Recoil `userState`/`lastUserIdState` 사용.
 *
 * ★ 인증 가드 계층이 없다: 이 화면은 라우터 단에서 막아 주는 공통 장치 없이,
 * 로그인 성공 시 직접 sessionStorage 에 accessToken/userId/role 을 저장하고 이동한다.
 */
import { LOGIN_ID_STORAGE_KEY } from "@/constants/sample-demo";
import Visibility from "@/components/icons/login/Visibility";
import InVisibility from "@/components/icons/login/InVisibility";
import React, { useState, useEffect } from "react";
// import Link from "next/link"; // 회원가입·찾기 링크 임시 비활성 중. 링크 되살릴 때 함께 복구.
import Checkbox from "@/components/signin/Checkbox";
import { useResetRecoilState, useRecoilState } from "recoil";
import { useRouter } from "next/router";
import authUser from "./api/sign/authUser";
import LoginFailModal from "@/components/modal/LoginFailModal";
import fetchUserInfo from "./api/home/fetchUserInfo";
import Header from "@/components/layout/Header";
import Head from "next/head";
import { lastUserIdState, userState } from "@/recoil/atom";
import { useClearQuizState } from "@/hooks/useClearQuizState";

/** 학생/선생님 역할 토글 + 아이디·비밀번호 로그인 폼. */
function LoginPage() {
  const router = useRouter();
  const [userId, setUserId] = useState("");
  const [userInfo, setUserInfo] = useRecoilState(userState);

  const [id, setId] = useState("");
  const [pw, setPw] = useState("");
  const [pwVisible, setPwVisible] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [idErr, setIdErr] = useState(false);
  const [pwErr, setPwErr] = useState(false);
  const [failModalOpen, setFailModalOpen] = useState(false);
  const [failMsg, setFailMsg] = useState("");
  // 역할 선택 토글. 기본은 학생. atom 없이 컴포넌트 로컬 state로 관리한다.
  const [selectedRole, setSelectedRole] = useState<"STUDENT" | "TEACHER">(
    "STUDENT"
  );

  const resetUserInfoState = useResetRecoilState(userState);
  const [lastUserId, setLastUserId] = useRecoilState(lastUserIdState);
  const { clearAllQuizState } = useClearQuizState();

  const handleIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setId(e.target.value);
  };

  const handlePwChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPw(e.target.value);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    loginClick();
  };

  const loginClick = async () => {
    if (!id || !pw) {
      setFailMsg("");
      setFailModalOpen(true);
      if (!id) setIdErr(true);
      if (!pw) setPwErr(true);
      return;
    }

    // 로그인 API 호출
    const res = await authUser(id, pw);
    if (res && res.isSuccess) {
      // 응답 role과 토글 선택값 비교. 불일치 시 sessionStorage 저장을 차단한다.
      // role은 DB의 진실(`User.role`)만이 권위이고, 토글 선택값은
      // "잘못된 화면에서 잘못된 토큰을 받지 않도록" 하는 UX 게이트다.
      const { userId, accessToken, role, needPasswordChange, name } = res.result;
      if (role !== selectedRole) {
        setFailMsg("선택한 역할과 계정 정보가 일치하지 않습니다.");
        setFailModalOpen(true);
        return;
      }

      // 서버에서 받은 유저 ID와 Access Token 설정
      sessionStorage.setItem("accessToken", accessToken);
      sessionStorage.setItem("userId", String(userId));
      // useAuthGuard가 storedRole === "TEACHER"만 분기하므로 학생도 명시 저장해
      // 정책 일관성을 유지한다(`useAuthGuard.ts` fallback과 호환).
      sessionStorage.setItem("role", role);
      // 체험(샘플) 계정 판정용. `useSampleDemo`가 읽는다.
      sessionStorage.setItem(LOGIN_ID_STORAGE_KEY, id);
      // 비밀번호 설정 화면의 "{이름} 학생, 환영해요" 메시지용. 서버가 이름을 안 주면 지워 둔다.
      if (name) {
        sessionStorage.setItem("userName", name);
      } else {
        sessionStorage.removeItem("userName");
      }
      // 초기 비밀번호 강제 변경 플래그. `PasswordChangeGate`(_app.tsx)가 이 키를 읽어
      // 플래그가 켜진 동안 모든 화면을 `/password-setup` 으로 되돌린다.
      // BE 가 필드를 아직 안 주는 과도기에는 undefined → "false" 로 저장해 게이트가 열리지 않게 한다.
      sessionStorage.setItem(
        "needPasswordChange",
        String(needPasswordChange === true)
      );
      setUserId(String(userId));
      setLastUserId(userId); // 마지막 로그인 유저 ID 업데이트
      if (needPasswordChange === true) {
        // replace: 히스토리에서 `/login` 을 밀어내, 변경 화면에서 뒤로가기를 눌러도 로그인 화면으로
        // 돌아가 세션이 끊기는 일이 없게 한다. 일반 로그인 분기는 기존대로 push.
        router.replace("/password-setup");
        return;
      }
      router.push(role === "TEACHER" ? "/teacher" : "/");
    } else {
      // 로그인 실패 처리
      if (res && res.code === 409) {
        setFailMsg("다른 기기에서 로그인 중입니다. 선생님을 불러주세요!");
      } else {
        setFailMsg("");
      }
      setFailModalOpen(true);
    }
  };

  useEffect(() => {
    // 로그인 페이지에 접속하면 Recoil 상태 및 퀴즈 데이터 초기화
    resetUserInfoState();
    clearAllQuizState();
    sessionStorage.clear();
  }, []);

  // 유저가 로그인 했을 경우, 유저정보 API 호출해 정보 리코일에 저장.
  useEffect(() => {
    const loadUserInfo = async () => {
      if (userId) {
        try {
          const result = await fetchUserInfo(userId);
          setUserInfo(result.userInfo); // 유저 정보를 Recoil 상태에 저장
        } catch (error) {
          console.error("Error fetching user info:", error);
        }
      }
    };
    loadUserInfo();
  }, [userId]);

  // 모달 닫기 함수
  const closeFailModal = () => {
    setFailModalOpen(false);
    setFailMsg("");
  };

  // 구현!! 체크박스 클릭 시, 로그인 유지 기능
  // 주의: 근데 이거는 로그인 버튼 클릭 시에 반영이 되어야 함
  const handleCheckClick = () => {};

  return (
    <div className="login-bg flex flex-col items-center">
      <Head>
        <title>해봄 - 로그인</title>
      </Head>
      <Header hidden />
      <LoginFailModal
        isOpen={failModalOpen}
        onClose={closeFailModal}
        message={failMsg}
      />

      <div className="mt-[40px] text-24 text-heading font-bold mb-[24px] font-heading">
        로그인
      </div>
      <div className="login-white-layout px-[80px] py-[60px]">
        <form onSubmit={handleSubmit} className="flex flex-col items-center gap-y-[40px] w-full">
          {/*
            역할 선택 토글 (세그먼트 컨트롤 2버튼).
            - role="group" + aria-label로 스크린리더에 그룹 의미 + 컨텍스트 노출
            - aria-pressed로 토글 상태 노출 (ARIA Authoring Practices Toggle Button)
            - 활성/비활성을 색(배경) + 텍스트 색 2축으로 구분(접근성 가드)
            - focus-visible:ring으로 키보드 포커스 가시성 명시
            - 토큰: blue-6 / gray-1·2·3·9·10 / white (새 hex 0)
          */}
          <div
            role="group"
            aria-label="로그인 역할 선택"
            className="flex gap-1 p-1 bg-page rounded-lg w-full"
          >
            <button
              type="button"
              aria-pressed={selectedRole === "STUDENT"}
              onClick={() => setSelectedRole("STUDENT")}
              className={`flex-1 h-[40px] rounded-md text-15 font-medium focus-visible:ring-2 focus-visible:ring-primary-focus focus-visible:outline-none transition-colors ${
                selectedRole === "STUDENT"
                  ? "bg-primary text-white"
                  : "bg-fill text-body hover:bg-fill-strong hover:text-strong"
              }`}
            >
              학생으로 로그인
            </button>
            <button
              type="button"
              aria-pressed={selectedRole === "TEACHER"}
              onClick={() => setSelectedRole("TEACHER")}
              className={`flex-1 h-[40px] rounded-md text-15 font-medium focus-visible:ring-2 focus-visible:ring-primary-focus focus-visible:outline-none transition-colors ${
                selectedRole === "TEACHER"
                  ? "bg-primary text-white"
                  : "bg-fill text-body hover:bg-fill-strong hover:text-strong"
              }`}
            >
              선생님으로 로그인
            </button>
          </div>

          <div>
            <div className="mb-[16px]">
              <input
                type="text"
                id="login-id"
                aria-label="아이디"
                aria-invalid={idErr}
                aria-describedby="login-id-desc"
                value={id}
                onChange={handleIdChange}
                autoComplete="username"
                className={`${idErr ? "login-input-alert " : "input-box"}`}
                placeholder="아이디 입력"
              ></input>
              {idErr ? (
                <div
                  id="login-id-desc"
                  role="alert"
                  className="login-alert-text mt-[8px]"
                >
                  아이디를 입력해 주세요.
                </div>
              ) : (
                <div id="login-id-desc" className="login-info-text mt-[8px]">
                  아이디를 입력해주세요.
                </div>
              )}
            </div>

            <div className="mb-[16px] relative">
              <input
                type={!pwVisible ? "password" : "text"}
                id="login-pw"
                aria-label="비밀번호"
                aria-invalid={pwErr}
                value={pw}
                onChange={handlePwChange}
                autoComplete="current-password"
                className={`${pwErr ? "password-input-alert" : "password-box"}`}
                placeholder="비밀번호 입력"
              ></input>
              <button
                type="button"
                onClick={() => setPwVisible(!pwVisible)}
                aria-pressed={pwVisible}
                aria-label={pwVisible ? "비밀번호 가리기" : "비밀번호 보기"}
                className="absolute top-4 right-4 cursor-pointer"
              >
                {pwVisible ? <Visibility /> : <InVisibility />}
              </button>
              {pwErr ? (
                <div className="login-alert-text mt-[8px]">
                  비밀번호를 입력해 주세요.
                </div>
              ) : (
                <div className="login-info-text mt-[8px]">
                  비밀번호를 입력해주세요.
                </div>
              )}
            </div>

            <div className="flex gap-x-2 items-center">
              <Checkbox
                checked={isChecked}
                onClick={() => setIsChecked(!isChecked)}
              />
              <div className="text-15 text-heading font-normal">
                로그인 유지
              </div>
            </div>
          </div>

          {/* 버튼들 */}
          <div className="flex flex-col gap-y-[10px] w-full">
            <button
              type="submit"
              className="blue-button h-[48px] focus-visible:ring-2 focus-visible:ring-primary-focus focus-visible:outline-none"
            >
              <span>로그인</span>
            </button>
            {/*
              회원가입 진입 임시 비활성.
              나중에 되살릴 예정이라 지우지 않고 주석으로 남긴다.
              되살릴 때는 이 블록의 주석만 벗기면 된다. /signin 라우트 자체는 그대로 살아 있다.

              <Link href="/signin">
                <div className="gray-button w-full h-[48px]">회원가입</div>
              </Link>
            */}
            {/*
              아이디/비밀번호 찾기 진입 임시 비활성 (2026-09-06).
              회원가입과 같은 방식 — 되살릴 때 이 블록의 주석만 벗긴다. /find 라우트 자체는 그대로 살아 있다.

              <Link href="/find">
                <div className="login-find-text">
                  아이디/비밀번호를 잊으셨나요?
                </div>
              </Link>
            */}
          </div>
        </form>
      </div>
    </div>
  );
}

export default LoginPage;
